import React from 'react';
import { faCog } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import OrganizationSelector from './OrganizationSelector';
import SystemStatusIndicator from './SystemStatusIndicator';
import {
  ADMIN_NAVIGATION,
  REGULAR_NAVIGATION,
  getNavigationWithCurrentState
} from '../shared/navigation';

export default function Sidebar(): React.ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, organizations } = useAuth();

  // Check if user is saasadmin
  const isSaasAdmin =
    user?.user_metadata?.global_role === 'saasadmin' ||
    organizations.some(org => org.role === 'saasadmin');

  // Check if we're in admin context
  const isAdminContext = location.pathname.startsWith('/admin');

  // Choose navigation based on context and add current state
  const baseNavigation = isAdminContext ? ADMIN_NAVIGATION : REGULAR_NAVIGATION;
  const navigation = getNavigationWithCurrentState(location.pathname, baseNavigation);

  return (
    <aside className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0">
      <div className="flex flex-col flex-grow bg-white border-r border-gray-200 overflow-y-auto">
        {/* Organization Selector - Only show in regular context */}
        {!isAdminContext && (
          <div className="flex-shrink-0">
            <OrganizationSelector />
          </div>
        )}

        {/* Admin Context Header */}
        {isAdminContext && (
          <div className="flex-shrink-0 p-4 border-b border-gray-200">
            <div className="flex items-center">
              <div className="flex items-center justify-center w-10 h-10 bg-red-100 rounded-lg">
                <FontAwesomeIcon icon={faCog} className="w-5 h-5 text-red-600" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-gray-900">Administração SaaS</h3>
                <p className="text-xs text-gray-500">Gestão do Sistema</p>
              </div>
            </div>
          </div>
        )}

        {/* Context Switch for SaaS Admins with Organizations */}
        {isSaasAdmin && organizations.length > 0 && (
          <div className="flex-shrink-0 p-2 border-b border-gray-200">
            <div className="flex rounded-md bg-gray-100 p-1">
              <button
                onClick={() => navigate('/')}
                className={`flex-1 text-xs font-medium py-2 px-3 rounded transition-colors ${
                  !isAdminContext
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Clínica
              </button>
              <button
                onClick={() => navigate('/admin')}
                className={`flex-1 text-xs font-medium py-2 px-3 rounded transition-colors ${
                  isAdminContext
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Admin
              </button>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="mt-6 flex-1 px-2 space-y-1">
          {navigation.map(item => (
            <button
              key={item.name}
              onClick={() => navigate(item.href)}
              className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md w-full text-left transition-colors ${
                item.current
                  ? 'bg-blue-50 border-r-4 border-blue-600 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <FontAwesomeIcon
                icon={item.icon}
                className={`mr-3 flex-shrink-0 h-5 w-5 ${
                  item.current ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'
                }`}
              />
              {item.name}
            </button>
          ))}
        </nav>

        {/* Bottom section */}
        <div className="flex-shrink-0 px-4 py-4 border-t border-gray-200 h-16">
          <SystemStatusIndicator showDetails={false} />
        </div>
      </div>
    </aside>
  );
}
